import { useEffect } from 'react';
import Footer from '../home/components/Footer';

export default function PaymentPayoutPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Payment & Payout Policy</h1>
          <p className="text-lg text-teal-50">For Providers</p>
          <p className="text-sm text-teal-100 mt-4">Last updated: 15 January 2025</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 mb-8">
            This Payment & Payout Policy explains how payments are processed, how provider earnings are calculated, 
            and how payouts are made on the Taskbudy platform. This policy forms part of the{' '}
            <a href="/provider-terms" className="text-teal-600 hover:text-teal-700 underline">
              Provider Terms & Conditions
            </a>.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. HOW PAYMENTS WORK</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>1.1</strong> Customers pay for jobs securely through the Taskbudy platform.</p>
              <p><strong>1.2</strong> Taskbudy processes customer payments using third-party payment service providers.</p>
              <p><strong>1.3</strong> Payments may take time to clear depending on the payment method, bank processing times, and fraud checks.</p>
              <p><strong>1.4</strong> Until payment is cleared, funds will appear as pending and are not withdrawable.</p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. COMMISSION & EARNINGS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>2.1</strong> Taskbudy charges a 15% commission on completed jobs.</p>
              <p><strong>2.2</strong> Commission is deducted only after a job is completed and payment clears.</p>
              <p><strong>2.3</strong> The remaining amount is credited to the Provider's dashboard as earnings.</p>
              <p><strong>2.4</strong> Commission is non-refundable except where required by law or where a job is refunded to a customer.</p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. PROVIDER BALANCE STATUS</h2>
            <p className="text-gray-700 mb-3">Provider balances may show one of the following statuses:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Pending</strong> – payment received but not yet cleared</li>
              <li><strong>Available</strong> – funds cleared and available for withdrawal</li>
              <li><strong>On hold</strong> – funds temporarily restricted due to disputes, chargebacks, compliance checks, or suspected misuse</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. PAYOUT OPTIONS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>4.1</strong> Providers may choose to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request a withdrawal manually once funds are available, or</li>
                <li>Receive automatic weekly payouts to their nominated bank account</li>
              </ul>
              <p><strong>4.2</strong> Payouts are subject to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Successful payment clearance</li>
                <li>Valid and verified payout details</li>
                <li>Compliance with these policies and the Provider Terms & Conditions</li>
              </ul>
              <p><strong>4.3</strong> Taskbudy is not responsible for delays caused by banks, payment processors, or incorrect payout details supplied by the Provider.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. FAILED PAYOUTS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>5.1</strong> If a payout fails due to incorrect bank details or other issues:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Funds will be returned to the Provider's available balance</li>
                <li>The Provider must update payout details before retrying</li>
              </ul>
              <p><strong>5.2</strong> Taskbudy may delay or cancel payouts where fraud, error, or misuse is suspected.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. DISPUTES, REFUNDS & CHARGEBACKS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>6.1</strong> If a customer raises a dispute or requests a refund:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The related earnings may be placed on hold while the issue is investigated</li>
              </ul>
              <p><strong>6.2</strong> If a refund or chargeback is approved:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The refunded amount and any associated fees may be deducted from the Provider's balance</li>
              </ul>
              <p><strong>6.3</strong> Where a Provider's balance is insufficient, Taskbudy reserves the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Offset future earnings, or</li>
                <li>Request repayment where legally permitted</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. OFF-PLATFORM PAYMENTS (PROHIBITED)</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>7.1</strong> Providers must not request or accept payment outside the Taskbudy platform for jobs sourced through Taskbudy.</p>
              <p><strong>7.2</strong> Attempting to bypass platform payments may result in:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Loss of earnings</li>
                <li>Account suspension or termination</li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. TAXES</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>8.1</strong> Providers are responsible for declaring and paying their own taxes.</p>
              <p><strong>8.2</strong> Taskbudy does not provide tax advice and does not deduct tax on behalf of Providers.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. POLICY CHANGES</h2>
            <p className="text-gray-700 mb-3">
              <strong>9.1</strong> Taskbudy may update this Payment & Payout Policy from time to time.
            </p>
            <p className="text-gray-700">
              <strong>9.2</strong> Continued use of the platform after changes take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. CONTACT</h2>
            <p className="text-gray-700">
              For payment or payout questions, Providers can contact Taskbudy support via the platform.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
